// Utilities to scope URLs to the current practice slug for Builder V2

// Check if we're currently on a custom domain
export const isCustomDomain = () => {
  if (typeof window === "undefined") return false

  const hostname = window.location.hostname
  // Custom domains are typically not localhost, 127.0.0.1, or the main heal.me domain
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1"
  const isMainDomain = hostname.includes("heal.me") || hostname.includes("healme.com")

  return !isLocalhost && !isMainDomain
}

// Path segments that are NOT practice slugs (builder/admin routes)
const NON_PRACTICE_PATH_SEGMENTS = new Set([
  "portal",
  "website_v2",
  "builder_pages",
  "builder_sections",
  "builder_themes",
  "admin",
  "new",
  "edit"
])

export const getPracticeSlug = (practice) => {
  // Prefer vanity_slug, then vanitySlug, then user.slug (all valid for public URLs)
  const vanitySlug = practice?.user?.vanity_slug || practice?.user?.vanitySlug
  if (vanitySlug && String(vanitySlug).trim()) {
    return String(vanitySlug).trim()
  }
  const userSlug = practice?.user?.slug
  if (userSlug && !/^\d+$/.test(String(userSlug))) {
    return String(userSlug).trim()
  }

  // Fallback to current path only when we're on a public page (not builder/portal)
  if (typeof window !== "undefined") {
    const path = window.location.pathname
    const parts = path.split("/").filter(Boolean)
    const firstSegment = parts[0] || ""

    if (parts.length >= 1 && !NON_PRACTICE_PATH_SEGMENTS.has(firstSegment)) {
      // Handle service URLs: /{practiceSlug}/services/{serviceId}
      const serviceMatch = path.match(/^\/([^/]+)\/services\/([^/]+)/)
      if (serviceMatch) {
        return serviceMatch[1]
      }

      // Handle other URLs: /{practiceSlug}/{pageSlug}
      return firstSegment
    }
  }

  return ""
}

// Helper function to detect if we're on a v2 site
export const isV2Site = (practice) => {
  // CRITICAL: This file is ONLY used by WebsiteBuilderV2 components
  // V1 sites use completely different navigation and URL handling

  // Primary check: practice must have v2 enabled
  if (practice?.v2 === true) return true

  // Secondary checks for edge cases
  if (typeof window !== "undefined") {
    // Check if we're on a custom domain (v2 sites can use custom domains)
    const isCustomDomain =
      !window.location.hostname.includes("heal.me") &&
      !window.location.hostname.includes("healme.com") &&
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"

    if (isCustomDomain) return true

    // Check if we're on a v2 page by looking at the URL structure
    const path = window.location.pathname
    // V2 sites use the pattern /{practice_slug}/{page_slug} for public domains
    const isV2Pattern = path.match(/^\/[^/]+\/[^/]+$/) // Matches /slug/page pattern

    // Additional check: make sure we're not on legacy routes
    // Only check the first path segment after the domain
    const pathSegments = path.split("/").filter(Boolean)
    const firstSegment = pathSegments[0] || ""

    // Special handling for /review route
    // /review can be either legacy (testimonials) or v2 (reviews page)
    // We need to distinguish based on context
    if (firstSegment === "review") {
      // If we're on a custom domain, /review is likely a v2 page
      if (isCustomDomain) {
        return true // This is a v2 site
      }
      // For public domains, /review without a practice slug is legacy
      // But /{practice_slug}/review is v2
      if (pathSegments.length === 1) {
        return false // Legacy testimonials route
      }
      // If it's /{practice_slug}/review, it's v2
      return true
    }

    const isLegacyRoute =
      // Admin routes
      firstSegment === "admin" ||
      // Portal routes
      firstSegment === "portal" ||
      // Public static pages
      firstSegment === "pro" ||
      firstSegment === "pro2" ||
      firstSegment === "reviews" ||
      firstSegment === "pro3" ||
      firstSegment === "contact" ||
      firstSegment === "wellness" ||
      firstSegment === "specialties" ||
      firstSegment === "questions-az" ||
      firstSegment === "practitioners-az" ||
      firstSegment === "about" ||
      firstSegment === "privacy" ||
      firstSegment === "terms" ||
      firstSegment === "robots" ||
      firstSegment === "demo_confirmed" ||
      firstSegment === "application_completed" ||
      firstSegment === "download_app" ||
      firstSegment === "lifer_checkout" ||
      firstSegment === "business_associate_agreement" ||
      firstSegment === "proplatform" ||
      firstSegment === "pro-platform" ||
      firstSegment === "websites-for-reiki-practitioners" ||
      firstSegment === "book_demo" ||
      firstSegment === "home" ||
      // Instant actions
      firstSegment === "i" || // instant reviews/appointments
      firstSegment === "r" || // review requests
      firstSegment === "c" || // confirm reviews
      firstSegment === "m" || // manage appointments
      // API routes
      firstSegment === "api" ||
      firstSegment === "webhooks" ||
      // System routes
      firstSegment === "rails" ||
      firstSegment === "assets" ||
      firstSegment === "packs" ||
      firstSegment === "graphiql" ||
      firstSegment === "graphql" ||
      // Content routes
      firstSegment === "articles" ||
      firstSegment === "questions" ||
      firstSegment === "quizzes" ||
      firstSegment === "quiz_results" ||
      firstSegment === "answers" ||
      // Booking/appointment routes
      firstSegment === "appointments" ||
      firstSegment === "my-reviews" ||
      firstSegment === "payment_methods" ||
      firstSegment === "checkout" ||
      firstSegment === "practitioner-search" ||
      firstSegment === "search" ||
      // Session/auth routes
      firstSegment === "sessions" ||
      firstSegment === "password_resets" ||
      firstSegment === "login" ||
      firstSegment === "logout" ||
      firstSegment === "settings" ||
      firstSegment === "photo" ||
      // Form routes
      firstSegment === "contacts" ||
      firstSegment === "testimonials" ||
      firstSegment === "intake_forms" ||
      // Migration routes
      firstSegment === "migrations" ||
      // Builder v2 admin routes (not public pages)
      firstSegment === "builder_pages" ||
      firstSegment === "builder_sections" ||
      firstSegment === "builder_themes" ||
      // Credential routes
      firstSegment === "credentials_requests" ||
      firstSegment === "issuers_requests" ||
      // Cronofy integration
      firstSegment === "cronofy" ||
      // Webflow routes
      firstSegment === "lp" ||
      // Confirm route
      firstSegment === "confirm" ||
      // Apply redirect
      firstSegment === "apply" ||
      // Sitemap
      firstSegment === "sitemaps" ||
      // Video upload routes
      firstSegment === "video-review" ||
      // Intercom
      firstSegment === "intercom"

    return isV2Pattern && !isLegacyRoute
  }

  // If practice object doesn't have v2 flag, don't apply the fix
  // This prevents any accidental application to v1 sites
  return false
}

// Helper function to get practice slug from current URL when practice object is not available
export const getPracticeSlugFromUrl = () => {
  if (typeof window === "undefined") return ""

  const path = window.location.pathname
  const parts = path.split("/").filter(Boolean)
  const firstSegment = parts[0] || ""

  // Don't treat builder/portal segments as practice slug
  if (parts.length >= 1 && !NON_PRACTICE_PATH_SEGMENTS.has(firstSegment)) {
    // Handle service URLs: /{practiceSlug}/services/{serviceId}
    const serviceMatch = path.match(/^\/([^/]+)\/services\/([^/]+)/)
    if (serviceMatch) {
      return serviceMatch[1]
    }

    // Handle other URLs: /{practiceSlug}/{pageSlug}
    return firstSegment
  }

  return ""
}

/** Public v2 service detail: /{practiceSlug}/services/{segment} or custom domain /services/{segment} (numeric id or slug) */
export const isBuilderV2PublicServiceDetailPath = () => {
  if (typeof window === "undefined") return false
  const path = window.location.pathname
  return /^\/[^/]+\/services\/[^/]+$/.test(path) || /^\/services\/[^/]+$/.test(path)
}

export const scopePath = (path, practice) => {
  if (!path) return "/"
  // Leave external links untouched
  if (/^https?:\/\//i.test(path)) return path

  const cleanPath = path.startsWith("/") ? path : `/${path}`
  const pathOnly = cleanPath.split(/[?#]/)[0]
  const pathOnlyLower = pathOnly.toLowerCase()

  const onCustomDomain = practice?.isCustomDomain === true || practice?.is_custom_domain === true || isCustomDomain()
  const slug = getPracticeSlug(practice)

  // heal.me: /{practice_slug}/home → /{practice_slug} (home is never .../home)
  if (slug && pathOnlyLower === `/${slug.toLowerCase()}/home`) {
    if (onCustomDomain) return "/"
    return `/${slug}`
  }

  // Bare /home → real home URL
  if (pathOnlyLower === "/home") {
    if (onCustomDomain) return "/"
    if (slug) return `/${slug}`
    return "/"
  }

  // Custom domain: URLs are {base_url}/page (no pro slug prefix). Use server flag first, then client detection.
  if (onCustomDomain) {
    return cleanPath
  }

  // For public domains, add the practice slug
  if (!slug) {
    // Only apply the fallback fix for v2 sites
    if (isV2Site(practice)) {
      // If we can't get the practice slug, try to get it from the current URL
      const currentSlug = getPracticeSlugFromUrl()
      if (currentSlug) {
        // Avoid double prefix if already scoped
        if (cleanPath.startsWith(`/${currentSlug}/`) || cleanPath === `/${currentSlug}`) return cleanPath
        return `/${currentSlug}${cleanPath}`
      }
      // Debug: Log when we can't get a practice slug for v2 sites
      console.warn("scopePath (v2): Could not determine practice slug for path:", path, "practice:", practice) // eslint-disable-line no-console
    }
    return cleanPath
  }
  // Avoid double prefix if already scoped
  if (cleanPath.startsWith(`/${slug}/`) || cleanPath === `/${slug}`) return cleanPath
  return `/${slug}${cleanPath}`
}

const isBuilderHomePageRecord = (page) =>
  Boolean(page && (page.defaultPageType === "home" || page.default_page_type === "home"))

export const practiceHasPublishedBlogPosts = (practice) =>
  Boolean(practice?.hasPublishedBlogPosts || practice?.has_published_blog_posts)

export const isBuilderBlogPage = (page) =>
  Boolean(page && (page.defaultPageType === "blog" || page.default_page_type === "blog"))

/** Header/footer nav: blog page link only when /blog would render (published posts + feature on). */
export const pageVisibleInBuilderNav = (page, practice) => {
  if (!page || page.hidden) return false
  if (isBuilderBlogPage(page) && !practiceHasPublishedBlogPosts(practice)) return false

  return true
}

/**
 * Public URL path for a builder page record. Home is always `/` (custom domain) or `/{pro_slug}` (heal.me), never `.../home`.
 */
export const publicPathForBuilderPage = (page, practice) => {
  if (!page) return scopePath("/", practice)
  if (isBuilderHomePageRecord(page)) {
    const onCustomDomain = practice?.isCustomDomain === true || practice?.is_custom_domain === true || isCustomDomain()
    if (onCustomDomain) return "/"
    const pracSlug = getPracticeSlug(practice)
    return pracSlug ? `/${pracSlug}` : "/"
  }
  return scopePath(`/${page.slug}`, practice)
}

/**
 * Normalize theme-stored nav hrefs (relative or absolute) so home is never published as /home.
 */
export const normalizeBuilderNavHref = (href, practice) => {
  if (href == null || typeof href !== "string") return href
  if (/^https?:\/\//i.test(href)) {
    try {
      const u = new URL(href)
      const normalized = scopePath(u.pathname, practice)
      return `${normalized}${u.search}${u.hash}`
    } catch {
      return href
    }
  }
  return scopePath(href, practice)
}

// Normalize a single stored header navigation link into the canonical
// { text, href } shape. Tolerates legacy entries that use { label, slug }
// (an older builder shape) and drops anything without a usable string href so
// callers never feed `undefined` into href-string helpers.
export const normalizeBuilderNavLink = (link, practice) => {
  if (!link || typeof link !== "object") return null

  const text = link.text ?? link.label
  let href = link.href
  if ((href == null || typeof href !== "string") && typeof link.slug === "string" && link.slug.length > 0) {
    href = scopePath(`/${link.slug}`, practice)
  }

  if (typeof href !== "string" || href.length === 0) return null

  return { ...link, text, href }
}

// Normalize a stored header navigationLinks array. Returns only the entries
// that resolve to a valid { text, href } link; callers should fall back to the
// dynamically generated navigation when the result is empty.
export const normalizeBuilderNavLinks = (links, practice) => {
  if (!Array.isArray(links)) return []
  return links.map((link) => normalizeBuilderNavLink(link, practice)).filter(Boolean)
}

const builderNavHrefKey = (href, practice) => {
  const normalized = normalizeBuilderNavHref(href, practice)
  return (normalized || href || "").replace(/\/+$/, "") || "/"
}

/** True when href (or link label) resolves to the practice home URL. */
export const isBuilderHomeNavHref = (href, practice, link = null) => {
  const text = link?.text ?? link?.label
  if (typeof text === "string" && text.trim().toLowerCase() === "home") {
    return true
  }

  if (href == null) return false

  const normalizedHref = normalizeBuilderNavHref(href, practice)
  const homeHref = scopePath("/", practice)

  if (builderNavHrefKey(normalizedHref, practice) === builderNavHrefKey(homeHref, practice)) {
    return true
  }

  const pathOnly = String(normalizedHref).split(/[?#]/)[0]
  const segments = pathOnly
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean)

  if (segments.length === 0) return true

  const slug = getPracticeSlug(practice)
  return Boolean(slug && segments.length === 1 && segments[0].toLowerCase() === slug.toLowerCase())
}

/**
 * Merge legacy theme-stored header nav with dynamically generated page links.
 * Dynamic links define which pages appear and their order (including custom pages);
 * stored links can override link text for matching hrefs.
 */
export const mergeBuilderNavLinks = (storedLinks, dynamicLinks, practice) => {
  const normalizedStored = normalizeBuilderNavLinks(storedLinks, practice)
  if (normalizedStored.length === 0) return dynamicLinks

  const storedByHref = new Map(normalizedStored.map((link) => [builderNavHrefKey(link.href, practice), link]))
  const dynamicHrefKeys = dynamicLinks.map((link) => builderNavHrefKey(link.href, practice))

  const merged = dynamicLinks.map((dynamicLink) => {
    const key = builderNavHrefKey(dynamicLink.href, practice)
    return storedByHref.get(key) || dynamicLink
  })

  // Keep stored-only links (e.g. legacy external entries) at the end.
  // Home is controlled by showHome via dynamicLinks — never re-append from stored nav.
  normalizedStored.forEach((storedLink) => {
    const key = builderNavHrefKey(storedLink.href, practice)
    if (dynamicHrefKeys.includes(key)) return
    if (isBuilderHomeNavHref(storedLink.href, practice, storedLink)) return

    merged.push(storedLink)
  })

  return merged
}

export const buildPublicPageUrl = (pageSlug, practice) => scopePath(`/${pageSlug}`, practice)

/** @returns {Array<{ id?: number, slug: string, title?: string, defaultPageType?: string, default_page_type?: string, hidden?: boolean, order?: number }>} */
export const builderPagesForPractice = (practice) => {
  const pages = practice?.pages
  return Array.isArray(pages) ? pages : []
}

export const findBuilderPageByDefaultType = (practice, defaultPageType) => {
  if (!defaultPageType) return null

  return (
    builderPagesForPractice(practice).find(
      (page) => (page.defaultPageType || page.default_page_type) === defaultPageType
    ) || null
  )
}

export const findBuilderPageBySlug = (practice, slug) => {
  if (!slug) return null

  return builderPagesForPractice(practice).find((page) => page.slug === slug) || null
}

/**
 * Resolve the current public slug for a default page (or legacy slug fallback when pages are not loaded).
 * @param {object|null|undefined} practice
 * @param {string|null|undefined} defaultPageType — e.g. services, reviews, contact
 * @param {string|null|undefined} legacySlug — pre-default_page_type slug (e.g. review for reviews)
 */
export const resolveBuilderPageSlug = (practice, defaultPageType, legacySlug) => {
  if (defaultPageType) {
    const page = findBuilderPageByDefaultType(practice, defaultPageType)
    if (page?.slug) return page.slug
  }

  if (legacySlug) {
    const bySlug = findBuilderPageBySlug(practice, legacySlug)
    if (bySlug?.slug) return bySlug.slug
    return legacySlug
  }

  return null
}

/** Public URL for a default builder page, using live slug from practice.pages when available. */
export const buildDefaultPageUrl = (practice, defaultPageType, legacySlug) => {
  const page =
    findBuilderPageByDefaultType(practice, defaultPageType) ||
    (legacySlug ? findBuilderPageBySlug(practice, legacySlug) : null)

  if (page) return publicPathForBuilderPage(page, practice)

  const slug = legacySlug || resolveBuilderPageSlug(practice, defaultPageType, null)
  return slug ? buildPublicPageUrl(slug, practice) : scopePath("/", practice)
}

const bookableEventTypesForPractice = (practice) => practice?.bookableEventTypes || practice?.bookable_event_types || []

const bookableEventsForPractice = (practice) => practice?.bookableEvents || practice?.bookable_events || []

/** @param {number|string|{ id?: number, publicSlug?: string, public_slug?: string }|null|undefined} serviceRef — numeric id, slug string, or service object with publicSlug */
export const buildServiceUrl = (serviceRef, practice, options = {}) => {
  // options: { query: string, anchor: string }
  const rawSegment = (() => {
    if (serviceRef == null) return null
    if (typeof serviceRef === "object") {
      return serviceRef.publicSlug || serviceRef.public_slug || serviceRef.id
    }
    return serviceRef
  })()

  const normalizedSegment =
    typeof rawSegment === "string" && rawSegment.startsWith("service:")
      ? rawSegment.replace(/^service:/, "")
      : rawSegment

  if (typeof rawSegment === "string" && rawSegment.startsWith("event:")) {
    const slug =
      resolveBookableEventTypeSlugForCta(rawSegment, practice) ||
      resolveBookableEventTypeSlugForCta(rawSegment.replace(/^event:/, ""), practice) ||
      rawSegment.replace(/^event:/, "")
    return scopePath(`/events/${slug}`, practice)
  }

  const eventSlug = resolveBookableEventTypeSlugForCta(rawSegment, practice)
  if (eventSlug) {
    return scopePath(`/events/${eventSlug}`, practice)
  }

  let path = normalizedSegment ? `/services/${normalizedSegment}` : `/services`
  if (options.query) {
    path += path.includes("?") ? `&${options.query}` : `?${options.query}`
  }
  if (options.anchor) {
    path += `#${options.anchor}`
  }
  return scopePath(path, practice)
}

/** Public bookable event type URL — always uses slug in the path (Rails route param is type_slug, not numeric id). */
export const buildEventTypeUrl = (eventTypeSlugOrId, practice) => {
  const resolved =
    resolveBookableEventTypeSlugForCta(eventTypeSlugOrId, practice) ||
    String(eventTypeSlugOrId || "")
      .trim()
      .replace(/^event:/, "")
  return scopePath(`/events/${resolved}`, practice)
}
export const buildBookingUrl = (practice) => buildDefaultPageUrl(practice, "services", "services")

/**
 * Public site root for absolute links (private service URLs, share links, etc.).
 * Mirrors Practice#url: custom domain → https://domain; else practice.url or heal.me/{slug}.
 */
export const resolvePublicSiteBaseUrl = (practice) => {
  if (!practice) return null

  if (practice.url) return String(practice.url).replace(/\/$/, "")

  const customDomain = practice.customDomain || practice.custom_domain
  if (customDomain) {
    const domain = String(customDomain)
      .replace(/^https?:\/\//i, "")
      .replace(/\/$/, "")
    return domain ? `https://${domain}` : null
  }

  const website = practice.website
  if (website && /^https?:\/\//i.test(String(website))) {
    return String(website).replace(/\/$/, "")
  }

  const slug = getPracticeSlug(practice)
  if (slug && typeof window !== "undefined") {
    return `${window.location.origin}/${slug}`
  }

  return null
}

/** Absolute public URL for a bookable service (custom domain, heal.me/{slug}, or dev). */
export const buildPublicServiceUrl = (service, practice) => {
  const segment = typeof service === "object" ? service.publicSlug || service.public_slug || service.id : service
  const path = segment ? `/services/${segment}` : "/services"

  const base = resolvePublicSiteBaseUrl(practice)
  if (base) return `${base}${path}`

  const scoped = buildServiceUrl(service, practice)
  if (typeof window !== "undefined") {
    if (/^https?:\/\//i.test(scoped)) return scoped
    return `${window.location.origin}${scoped.startsWith("/") ? scoped : `/${scoped}`}`
  }

  return scoped
}

/**
 * Resolve Hero / CTA "Service or Event" targets that refer to a bookable **event type** URL (`/events/:slug`).
 *
 * `bookable_event_types.id` and `bookable_events.id` come from different tables — numeric IDs can collide.
 * Legacy UI also merged types + scheduled instances into one list with non-unique option values, which broke
 * Array.prototype.find (types were checked first) and HTML selects with duplicate values.
 *
 * @param {string|number} ctaTarget — stored segment from section settings (service id, event type slug, or legacy numeric id)
 * @param {object} practice — builder/public practice payload with bookableEventTypes / bookableEvents
 * @returns {string|null} event type slug for buildEventTypeUrl, or null if this target is not an event
 */
export const resolveBookableEventTypeSlugForCta = (ctaTarget, practice) => {
  if (ctaTarget == null || ctaTarget === "") return null

  const initial = String(ctaTarget).trim()
  if (!initial) return null

  if (initial.startsWith("service:")) {
    return null
  }

  const types = bookableEventTypesForPractice(practice)
  const instances = bookableEventsForPractice(practice)
  const raw = initial.startsWith("event:") ? initial.replace(/^event:/, "") : initial

  const typeBySlug = types.find((t) => t.slug === raw)
  if (typeBySlug?.slug) return typeBySlug.slug

  if (/^\d+$/.test(raw)) {
    // Hero / editor targets use bookable_event_types.id — resolve that before bookable_events.id (different tables; IDs can collide).
    const typeById = types.find((t) => String(t.id) === raw)
    if (typeById?.slug) return typeById.slug

    const instance = instances.find((e) => String(e.id) === raw)
    const fromInstance = instance?.bookableEventTypeSlug || instance?.bookable_event_type_slug
    if (fromInstance) return fromInstance
  }

  return null
}

/**
 * Open an external URL in a new tab after sanitizing/validating http(s).
 * Prefixes https:// when missing. No-ops on blank, invalid, or non-http(s) URLs.
 *
 * @param {string|null|undefined} rawUrl
 */
export const openExternalUrl = (rawUrl) => {
  if (rawUrl == null || String(rawUrl).trim() === "") return
  let sanitizedUrl = String(rawUrl).trim()
  if (!sanitizedUrl.startsWith("http://") && !sanitizedUrl.startsWith("https://")) {
    sanitizedUrl = `https://${sanitizedUrl}`
  }
  try {
    const parsed = new URL(sanitizedUrl)
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return
    window.open(sanitizedUrl, "_blank", "noopener,noreferrer")
  } catch {
    // invalid URL — no-op
  }
}
